import { useColorScheme as useNativeWindColorScheme } from 'nativewind';
import { useSyncExternalStore } from 'react';
import { Platform } from 'react-native';

export type ColorScheme = 'light' | 'dark';

// Web override: read the resolved color scheme from
// `<html data-theme="...">` (set in apps/web/src/main.tsx). This lets
// the app pin a theme regardless of the user's OS preference, which
// react-native-web's `useColorScheme` would otherwise read from
// `prefers-color-scheme` only. Native (iOS / Android / macOS) keeps
// using the platform hook so OS-level Dynamic Type / Dark Mode wins.
function subscribeToHtmlDataTheme(notify: () => void): () => void {
  if (typeof document === 'undefined') return () => {};
  const observer = new MutationObserver(notify);
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['data-theme'],
  });
  return () => observer.disconnect();
}

function readHtmlDataTheme(): ColorScheme | null {
  if (typeof document === 'undefined') return null;
  const v = document.documentElement.getAttribute('data-theme');
  return v === 'dark' || v === 'light' ? v : null;
}

export function useColorScheme(): ColorScheme {
  // Hooks must be called unconditionally; useSyncExternalStore here is
  // a no-op on native because `document` is undefined. We still call
  // both hooks every render, then pick a return value at the end.
  const htmlTheme = useSyncExternalStore(
    subscribeToHtmlDataTheme,
    readHtmlDataTheme,
    () => null,
  );
  // On native (macOS desktop — the only non-web platform that consumes
  // packages/ui) read NativeWind's colorScheme observable, NOT
  // react-native's `useColorScheme` (which reads the OS `Appearance` and
  // is unreliable at first paint on RN-macOS). The desktop shell forces
  // the scheme via `colorScheme.set(...)` (boot + theme toggle), which
  // also drives every `dark:` class. Reading the same observable here
  // keeps the StyleSheet path (`createTheme(useColorScheme())`) in lock-
  // step with the class path instead of the two resolving to different
  // schemes (the "split-brain" that rendered light bubbles on a dark
  // canvas). See docs/desktop-parity/02-message-rendering.md.
  const { colorScheme: nwScheme } = useNativeWindColorScheme();

  if (Platform.OS === 'web' && htmlTheme !== null) {
    return htmlTheme;
  }
  // NativeWind can briefly report `undefined` before the boot-time
  // `colorScheme.set('dark')` settles; the desktop app is dark-first, so
  // default there rather than flashing a light frame.
  return nwScheme === 'light' ? 'light' : 'dark';
}
